import { Component, OnDestroy, OnInit } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, filter, map, Observable, Subscription } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';

import {
  ButtonSize,
  ButtonTheme,
  IconButtonTheme,
  IconButtonSize,
  IconPosition,
  LoaderColor,
  LoaderTheme,
  ModalButtons,
} from '@fcom/ui-components';
import { UpgradeOfferState } from '@fcom/common-booking';
import { selectedUpgradeOffer } from '@fcom/common-booking/store/selectors/upgrade-offer.selector';
import { isPresent, unsubscribe } from '@fcom/core/utils';
import { FareFamily, FinnairCart } from '@fcom/dapi/api/models';
import { Amount, CartStatus } from '@fcom/dapi';
import { finShare } from '@fcom/rx';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces';
import { GtmService } from '@fcom/common/gtm';
import { BookingAppState } from '@fcom/common/interfaces/booking';

import { cartData, cartStatus } from '../../../../store/selectors';

enum UpgradeStatus {
  INIT,
  LOADING,
  READY,
  IN_PROGRESS,
  DONE,
  HIDDEN,
}

const GA_ELEMENT_NAME = 'booking-flow-upsell';

@Component({
  selector: 'fin-upgrade-promotion',
  templateUrl: 'upgrade-promotion.component.html',
  styleUrls: ['upgrade-promotion.component.scss'],
  standalone: false,
})
export class UpgradePromotionComponent implements OnInit, OnDestroy {
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconPosition = IconPosition;
  readonly LoaderColor = LoaderColor;
  readonly LoaderTheme = LoaderTheme;
  readonly ModalButtons = ModalButtons;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly UpgradeStatus = UpgradeStatus;

  upgradeOffer$: Observable<UpgradeOfferState>;
  cartData$: Observable<FinnairCart>;
  upgradePriceDifference$: Observable<Amount>;
  upgradePriceDiffTrend$: Observable<boolean>;
  currentTotalPrice$: Observable<Amount>;
  status$ = new BehaviorSubject(UpgradeStatus.INIT);
  loaded$: Observable<boolean>;

  selectedBenefitKey$: Observable<string>;
  suggestedBenefitKey$: Observable<string>;

  upgradeModalOpen = false;
  fareFamilyModalOpen = false;

  subscriptions = new Subscription();

  constructor(
    private store$: Store<BookingAppState>,
    private gtmService: GtmService
  ) {}

  ngOnInit(): void {
    this.upgradeOffer$ = this.store$.pipe(
      selectedUpgradeOffer(),
      filter((offer) => isPresent(offer?.upgradeFareFamily)),
      finShare()
    );
    this.cartData$ = this.store$.pipe(cartData(), finShare());

    this.currentTotalPrice$ = this.cartData$.pipe(map((cart) => cart.prices.unpaid?.total?.total.totalAmount));

    this.upgradePriceDifference$ = combineLatest([this.upgradeOffer$, this.currentTotalPrice$]).pipe(
      filter(([_offer, currentPrice]) => isPresent(currentPrice)),
      map(([upgradeOffer, currentPrice]) => ({
        amount: String(upgradeOffer.totalUpgradePrice - parseFloat(currentPrice.amount)),
        currencyCode: currentPrice.currencyCode,
      }))
    );
    this.upgradePriceDiffTrend$ = this.upgradePriceDifference$.pipe(map((diff) => parseFloat(diff.amount) >= 0));

    this.selectedBenefitKey$ = combineLatest([this.upgradeOffer$, this.cartData$]).pipe(
      map(([offer, cart]) => {
        const origin = cart.locations[cart.bounds[0].departure.locationCode];
        return this.getSelectedBenefitKey(offer.currentFareFamily, origin.countryCode);
      })
    );
    this.suggestedBenefitKey$ = this.upgradeOffer$.pipe(
      map((offer) => this.getSuggestedBenefitKey(offer.upgradeFareFamily))
    );

    this.setStatusListeners();
  }

  setStatusListeners(): void {
    // When upgradeOffer is found in store, set status as loading
    this.subscriptions.add(
      combineLatest([this.status$, this.upgradeOffer$])
        .pipe(filter(([upgradeStatus, offer]) => upgradeStatus === UpgradeStatus.INIT && !!offer.upgradeFareFamily))
        .subscribe(() => this.status$.next(UpgradeStatus.LOADING))
    );

    // When total prices has been calculated, set status as ready
    this.subscriptions.add(
      combineLatest([this.status$, this.currentTotalPrice$, this.upgradePriceDiffTrend$])
        .pipe(filter(([upgradeStatus]) => upgradeStatus === UpgradeStatus.LOADING))
        .subscribe(() => {
          this.gtmService.trackElement(
            GA_ELEMENT_NAME,
            GaContext.BOOKING_FLOW,
            ElementTypes.BANNER,
            'view',
            ElementActions.VIEW
          );
          this.status$.next(UpgradeStatus.READY);
        })
    );

    // When cart is updated, set status as done
    this.subscriptions.add(
      this.store$
        .pipe(
          cartStatus(),
          withLatestFrom(this.status$),
          filter(
            ([currentCartStatus, upgradeStatus]) =>
              currentCartStatus === CartStatus.OK && upgradeStatus === UpgradeStatus.IN_PROGRESS
          )
        )
        .subscribe(() => this.status$.next(UpgradeStatus.DONE))
    );
  }

  getSelectedBenefitKey(fareFamily: FareFamily, countryCode: string): string {
    const name = fareFamily.brandName.toLowerCase();
    if (name.includes('campaign')) {
      if (name.includes('light')) {
        return 'campaignLight';
      }
      if (name.includes('classic')) {
        return 'campaignClassic';
      }
    }
    if (name.includes('saver')) {
      return 'saver';
    }
    const japanOrigin = countryCode === 'JP';
    if (name.includes('classic')) {
      if (japanOrigin) {
        return 'classicJp';
      }
      return 'classic';
    }
    return 'light';
  }

  getSuggestedBenefitKey(fareFamily: FareFamily): string {
    const name = fareFamily.brandName.toLowerCase();
    if (name.includes('flex')) {
      return 'flex';
    }
    return 'classic';
  }

  openUpgradeModal(event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.gtmService.trackElement(
      GA_ELEMENT_NAME,
      GaContext.BOOKING_FLOW,
      ElementTypes.BUTTON,
      'open-modal',
      ElementActions.CLICK
    );
    this.upgradeModalOpen = true;
  }

  openFareFamilyModal(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.fareFamilyModalOpen = true;
  }

  confirmUpgrade(): void {
    this.upgradeModalOpen = false;
    this.status$.next(UpgradeStatus.IN_PROGRESS);
    this.gtmService.trackElement(
      GA_ELEMENT_NAME,
      GaContext.BOOKING_FLOW,
      ElementTypes.BUTTON,
      'confirm',
      ElementActions.CLICK
    );
    // TODO: How to update cart?
  }

  hide(): void {
    this.status$.next(UpgradeStatus.HIDDEN);
  }

  closeModals(): void {
    this.upgradeModalOpen = false;
    this.fareFamilyModalOpen = false;
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscriptions);
  }
}
